import { lazy } from 'react';
import type { ReactNode } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import RequirePermission from '@/auth/RequirePermission';
import { Perm } from '@/auth/permissions';
import RequireModule from '@/core/modules/RequireModule';
import { Modules } from '@/core/modules/moduleKeys';

import { sections } from './sections';
import type { HrSectionDef } from './pages/HrSectionPage';

// Pages and tabs load when first opened (NFR-04, docs §169).
const EmployeePage = lazy(() => import('./pages/EmployeePage'));
const HrHomePage = lazy(() => import('./pages/HrHomePage'));
const HrSectionPage = lazy(() => import('./pages/HrSectionPage'));
const PayrollRunPage = lazy(() => import('./pages/PayrollRunPage'));

/**
 * The HR module's routes, spread into the signed-in layout. Every page sits behind `RequireModule`,
 * so an organisation without the module sees the "not enabled" card rather than pages that would
 * only meet 403s. The URL mirrors the menu: the area, then the section, then the leaf.
 *
 * Self-service is Annova Me (`/me`, in `modules/me`), with no permission gate, only the module;
 * `/my-hr` redirects there. The public careers page sits outside the signed-in layout entirely.
 */
const guard = (element: ReactNode) => (
  <RequirePermission permission={Perm.Hr.View}>
    <RequireModule module={Modules.Hr}>{element}</RequireModule>
  </RequirePermission>
);

const section = <K extends string>(def: HrSectionDef<K>, path: string): RouteObject =>
  ({ path, element: guard(<HrSectionPage section={def as unknown as HrSectionDef} />) });

export const hrRoutes: RouteObject[] = [
  { path: 'hr', element: guard(<HrHomePage />) },

  { path: 'hr/people', element: <Navigate to="/hr/people/employees" replace /> },
  section(sections.employees, 'hr/people/employees/:view?'),
  { path: 'hr/people/employee/:id', element: guard(<EmployeePage />) },
  section(sections.structure, 'hr/people/structure/:view?'),
  section(sections.actions, 'hr/people/actions/:view?'),

  { path: 'hr/leave', element: <Navigate to="/hr/leave/requests" replace /> },
  section(sections.leaveRequests, 'hr/leave/requests/:view?'),
  section(sections.leaveBalances, 'hr/leave/balances/:view?'),
  section(sections.holidays, 'hr/leave/holidays/:view?'),

  { path: 'hr/time', element: <Navigate to="/hr/time/attendance" replace /> },
  section(sections.attendance, 'hr/time/attendance/:view?'),
  section(sections.overtime, 'hr/time/overtime/:view?'),

  { path: 'hr/payroll', element: <Navigate to="/hr/payroll/runs" replace /> },
  section(sections.payrollRuns, 'hr/payroll/runs/:view?'),
  { path: 'hr/payroll/run/:id', element: guard(<PayrollRunPage />) },
  section(sections.payrollProcessing, 'hr/payroll/processing/:view?'),
  section(sections.payrollLoans, 'hr/payroll/loans/:view?'),
  section(sections.payrollOutput, 'hr/payroll/output/:view?'),
  section(sections.payrollSetup, 'hr/payroll/setup/:view?'),

  section(sections.recruitment, 'hr/recruitment/:view?'),
  section(sections.development, 'hr/development/:view?'),
  section(sections.relations, 'hr/relations/:view?'),
  section(sections.compliance, 'hr/compliance/:view?'),
  section(sections.experience, 'hr/experience/:view?'),
  section(sections.workforce, 'hr/workforce/:view?'),
  section(sections.reports, 'hr/reports/:view?'),

  section(sections.settings, 'hr/settings/:view?'),
];
